import {
  LEGACY_ABILITY_COST,
  MAX_UPGRADE_LEVEL,
  MAX_SHIP_TIER,
  SHIP_TIERS,
  TIER_COSTS,
  upgradeCost,
} from '../game/constants/ships';
import { resolvedUpgradeForSlot, upgradeForPath, upgradeLabel } from '../game/constants/upgrades';
import { canEvolve } from '../game/constants/ship-tree';
import { CHAT, END_GAME, NOTIFICATION } from '../game/constants/hud';
import { BASE_START_HEALTH, MAX_LEADERBOARD_ENTRIES, UNNAMED_NAME } from '../game/constants/world';
import type { Camera } from '../render/camera';
import type { SpectatorCamera } from '../game/spectator-camera';
import type { ChatLine, MatchInfoState, World } from '../game/world';
import type { SurrenderView } from '../game/surrender';
import type { TeamStatsView } from '../game/team-stats';
import { socketState } from './socket-state.svelte';
import { eventAnnouncement } from './format';

export interface DockSlot {
  readonly slot: number;
  readonly name: string;
  readonly abbr: string;
  readonly level: number;
  readonly cost: number | null;
  readonly affordable: boolean;
}

export interface LeaderboardRowView {
  readonly nameId: number;
  readonly name: string;
  readonly team: number;
  readonly points: number;
}

export class HudState {
  playing = $state(false);
  spectating = $state(false);
  following = $state(false);
  followingName = $state('');
  viewWidth = $state(0);
  dead = $state(false);
  respawnSeconds = $state(0);
  deathTip = $state('');
  countdownSeconds = $state(0);
  returnToMenuRequested = $state(false);
  fps = $state(0);

  /** `cssScale` must be `uiScale / camera.pixelRatio`, not `devicePixelRatio` — DOM is laid out in CSS pixels, and using devicePixelRatio drifts on every retina display. */
  uiWidth = $state(0);
  uiHeight = $state(0);
  cssScale = $state(1);

  scrap = $state(0);
  blueBaseHealth = $state(BASE_START_HEALTH);
  redBaseHealth = $state(BASE_START_HEALTH);
  baseMaxHealth = $state(BASE_START_HEALTH);

  abilityName = $state('');
  abilityCost = $state<number | null>(null);
  abilityAffordable = $state(true);
  abilityRemainingMs = $state(0);
  abilityFraction = $state(1);
  drones = $state(0);
  maxDrones = $state(0);

  dock = $state<readonly DockSlot[]>([]);

  selfShipId = $state(0);
  selfTeam = $state(2);

  evolveScreenOpen = $state(false);
  evolutionAbilities = $state<Readonly<Record<number, readonly number[]>>>({});

  leaderboard = $state<readonly LeaderboardRowView[]>([]);
  myRank = $state(0);

  notification = $state('');
  chat = $state<readonly ChatLine[]>([]);

  chatEnabled = $state(false);

  lobbyOpen = $state(false);

  endGame = $state<MatchInfoState | null>(null);

  surrender = $state<SurrenderView | null>(null);

  teamStats = $state<TeamStatsView | null>(null);

  teamStatsOpen = $state(true);

  eventLabel = $state('');
  eventCountdown = $state('');

  now = $state(0);

  readonly abilityReady = $derived(this.abilityRemainingMs <= 0);

  /** Gated only on the ship having somewhere to evolve to, not on affordability — the server decides what can be paid for, and this client's quoted price can lag it by a frame. */
  toggleEvolveScreen(): void {
    this.evolveScreenOpen = this.evolveScreenOpen ? false : canEvolve(this.selfShipId);
  }

  sync(world: World, camera: Camera, now: number, spectator?: SpectatorCamera): void {
    this.now = now;
    this.playing = world.playing;
    this.spectating = world.spectating;
    this.dead = world.dead;
    this.respawnSeconds = world.dead
      ? Math.max(0, Math.ceil((world.respawnDelayMs - (now - world.diedAt)) / 1000))
      : 0;
    this.deathTip = world.deathTip;
    this.returnToMenuRequested = world.returnToMenuRequested;
    this.countdownSeconds = Math.max(
      0,
      Math.ceil((world.matchCountdownMs - (now - world.matchCountdownAt)) / 1000),
    );

    this.uiWidth = camera.uiWidth;
    this.uiHeight = camera.uiHeight;
    this.cssScale = camera.uiScale / camera.pixelRatio;

    this.scrap = world.scrap;
    this.blueBaseHealth = Math.max(0, world.blueBaseHealth);
    this.redBaseHealth = Math.max(0, world.redBaseHealth);
    this.baseMaxHealth = world.world.baseMaxHealth;

    const remaining = world.abilityCooldownRemaining(now);
    this.abilityRemainingMs = remaining;
    this.abilityFraction =
      world.abilityCooldownMs <= 0 ? 1 : Math.min(1, 1 - remaining / world.abilityCooldownMs);
    this.abilityName = upgradeLabel(upgradeForPath(world.selfShipId, 0)).name;
    const abilityCost = LEGACY_ABILITY_COST[world.selfShipId] ?? 0;
    this.abilityCost = abilityCost > 0 ? abilityCost : null;
    this.abilityAffordable = abilityCost <= 0 || world.scrap >= abilityCost;
    this.drones = world.selfDrones;
    this.maxDrones = world.selfMaxDrones;

    this.selfShipId = world.selfShipId;
    this.selfTeam = world.selfTeam;
    this.evolutionAbilities = world.evolutionAbilityIds;
    this.dock = buildDock(world);

    if (!this.playing || this.dead || this.spectating || !canEvolve(world.selfShipId)) {
      this.evolveScreenOpen = false;
    }
    this.leaderboard = buildLeaderboard(world);
    this.myRank = world.myRank;

    const latest = world.notifications.at(-1);
    this.notification =
      latest !== undefined && now - latest.at < NOTIFICATION.lifetimeMs ? latest.text : '';

    this.chat = world.chat.slice(-CHAT.maxLines).reverse();
    this.chatEnabled = world.chatEnabled;
    this.lobbyOpen = world.lobby !== null && !world.lobby.started;

    this.endGame =
      world.matchInfo !== null && now - world.matchInfoAt < END_GAME.lifetimeMs
        ? world.matchInfo
        : null;

    const event = eventAnnouncement(world.gameModeStartAt, world.gameModeEndAt, now);
    this.eventLabel = event.label;
    this.eventCountdown = event.countdown;

    this.surrender = world.surrender;
    this.teamStats = world.teamStats;

    const watched = spectator === undefined || !this.spectating ? null : spectator.followId;
    this.following = watched !== null;
    this.followingName = watched === null ? '' : (spectator?.followedName(world) ?? '');
    this.viewWidth = this.spectating ? Math.round(camera.worldWidth) : 0;

    socketState.observe(world);
  }
}

function buildDock(world: World): readonly DockSlot[] {
  const tier = SHIP_TIERS[world.selfShipId] ?? 0;
  const slots: DockSlot[] = [];

  for (let slot = 0; slot < 3; slot++) {
    const level = world.selfLevels[slot] ?? 0;
    const cost = level >= MAX_UPGRADE_LEVEL ? null : upgradeCost(tier, level);
    const abilityId = resolvedUpgradeForSlot(world.selfShipId, slot, world.selfAbilityIds);
    const label = upgradeLabel(abilityId);
    slots.push({
      slot,
      name: label.name,
      abbr: label.abbr,
      level: level + 1,
      cost,
      affordable: cost !== null && world.scrap >= cost,
    });
  }

  /** Evolve cost is the tier cost less what has already gone into path upgrades — omitting `moneySpentOnUpgrades` quotes a price the server does not charge. */
  const evolveCost =
    tier >= MAX_SHIP_TIER
      ? null
      : Math.max((TIER_COSTS[tier] ?? 0) - world.moneySpentOnUpgrades, 0);
  slots.push({
    slot: 3,
    name: 'Upgrade Ship',
    abbr: 'Ship',
    level: tier + 1,
    cost: evolveCost,
    affordable: evolveCost !== null && world.scrap >= evolveCost,
  });

  return slots;
}

function buildLeaderboard(world: World): readonly LeaderboardRowView[] {
  return world.leaderboard.slice(0, MAX_LEADERBOARD_ENTRIES).map((entry) => {
    const name = world.nameCache.get(entry.nameId);
    return {
      nameId: entry.nameId,
      name: name === undefined || name === '' ? UNNAMED_NAME : name,
      team: entry.team,
      points: entry.points,
    };
  });
}
